import React from 'react';
import { View, StyleSheet, ViewStyle, TextStyle } from 'react-native';
import { Text } from '@/components/common/text';
import { SVG_COMPONENTS } from '@/assets/admin';
import { useAdminColors } from '@/hooks/admin/useAdminColors';
import { useThemedStyles } from '@/hooks/shared/useThemeColors';

type SvgKey = keyof typeof SVG_COMPONENTS;

interface InlineEmptyMessageProps {
  title: string;
  message?: string;
  icon?: SvgKey;
  iconSize?: number;
  iconColor?: string;
  containerStyle?: ViewStyle;
  titleStyle?: TextStyle;
  messageStyle?: TextStyle;
}

export const InlineEmptyMessage: React.FC<InlineEmptyMessageProps> = ({
  title,
  message,
  icon = 'EMPTY_MESSAGE',
  iconSize = 64,
  iconColor,
  containerStyle,
  titleStyle,
  messageStyle,
}) => {
  const styles = useStyles();
  const { redesign } = useAdminColors();
  const resolvedIconColor = iconColor ?? redesign.text.muted;
  const IconComponent = SVG_COMPONENTS[icon];

  // Display only, so let taps pass through: on web this transparent box is
  // hit-testable wherever its container places it, and callers offset it over
  // the controls above (the Messages filter tabs went dead behind it).
  return (
    <View style={[styles.container, containerStyle]} pointerEvents="none">
      {!!IconComponent && (
        <IconComponent width={iconSize} height={iconSize} color={resolvedIconColor} style={styles.icon} />
      )}
      <Text scale="content" style={[styles.title, titleStyle]}>{title}</Text>
      {message ? <Text scale="content" style={[styles.message, messageStyle]}>{message}</Text> : null}
    </View>
  );
};

const useStyles = () =>
  useThemedStyles(({ components, colors }) => {
    const admin = components.admin;
    return StyleSheet.create({
      container: {
        flex: 1,
        justifyContent: 'center',
        alignItems: 'center',
        paddingHorizontal: 24,
      },
      icon: {
        marginBottom: 16,
      },
      title: {
        fontSize: 16,
        fontWeight: '600',
        color: admin.dataTable.text,
        textAlign: 'center',
        marginBottom: 8,
      },
      message: {
        fontSize: 14,
        color: colors.text.dim,
        textAlign: 'center',
        lineHeight: 20,
        maxWidth: 300,
      },
    });
  });
